import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../app/components/SnackBar';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import i18n from '../../../../i18n/i18n.module';
import type { LessonOffering, LessonPackage } from '../../application/types';

function authorizationHeader(token?: string | null): string | undefined {
  if (!token) return undefined;
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function apiBaseUrl(): string {
  const host = getApiHost();
  return host.endsWith('/') ? host : `${host}/`;
}

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function unwrapRecord(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') return null;
  if ('data' in response) {
    const data = (response as { data?: unknown }).data;
    if (data && typeof data === 'object') return data as Record<string, unknown>;
  }
  return response as Record<string, unknown>;
}

function parsePackage(value: unknown): LessonPackage | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (row.id == null) return null;
  return {
    id: asNumber(row.id),
    lessonOfferingId: asNumber(row.lessonOfferingId),
    quantity: asNumber(row.quantity),
    discountPercent: asNumber(row.discountPercent),
    regularPrice: asNumber(row.regularPrice),
    savings: asNumber(row.savings),
    price: asNumber(row.price),
    pricePerLesson: asNumber(row.pricePerLesson),
    currency: String(row.currency ?? ''),
    status: String(row.status ?? ''),
  };
}

function parseOffering(value: unknown): LessonOffering | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (row.id == null) return null;
  const packages = Array.isArray(row.packages)
    ? row.packages.flatMap((item) => {
        const parsed = parsePackage(item);
        return parsed ? [parsed] : [];
      })
    : [];
  return {
    id: asNumber(row.id),
    mentorId: asNumber(row.mentorId),
    type: String(row.type ?? ''),
    title: String(row.title ?? ''),
    description: row.description == null ? null : String(row.description),
    durationMinutes: asNumber(row.durationMinutes),
    price: asNumber(row.price),
    currency: String(row.currency ?? ''),
    status: String(row.status ?? ''),
    packages,
  };
}

function parseOfferingList(response: unknown): LessonOffering[] {
  const body = unwrapRecord(response);
  const list = body?.list;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    const parsed = parseOffering(item);
    return parsed ? [parsed] : [];
  });
}

export const lessonOfferingsApi = createApi({
  reducerPath: 'lessonOfferingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      const value = authorizationHeader(token);
      if (value) headers.set('Authorization', value);
      return headers;
    },
  }),
  tagTypes: ['LessonOffering'],
  endpoints: (build) => ({
    getMyLessonOfferings: build.query<LessonOffering[], void>({
      query: () => ({ url: 'mentors/me/lesson-offerings', method: 'GET' }),
      transformResponse: parseOfferingList,
      providesTags: ['LessonOffering'],
    }),
    createLessonOffering: build.mutation<
      LessonOffering,
      { title: string; durationMinutes: number; price: number; currency: string }
    >({
      query: (body) => ({
        url: 'mentors/me/lesson-offerings',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        parseOffering(unwrapRecord(response)) as LessonOffering,
      invalidatesTags: ['LessonOffering'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('lesson.saved'));
        } catch {
          showErrorToast(i18n.t('lesson.save_error'), undefined, 5000);
        }
      },
    }),
    updateLessonOffering: build.mutation<
      LessonOffering,
      {
        id: number;
        title: string;
        durationMinutes: number;
        price: number;
        currency: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `mentors/me/lesson-offerings/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) =>
        parseOffering(unwrapRecord(response)) as LessonOffering,
      invalidatesTags: ['LessonOffering'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('lesson.saved'));
        } catch {
          showErrorToast(i18n.t('lesson.save_error'), undefined, 5000);
        }
      },
    }),
    createLessonPackage: build.mutation<
      LessonPackage,
      { offeringId: number; quantity: number; discountPercent: number }
    >({
      query: ({ offeringId, ...body }) => ({
        url: `mentors/me/lesson-offerings/${offeringId}/packages`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        parsePackage(unwrapRecord(response)) as LessonPackage,
      invalidatesTags: ['LessonOffering'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('lesson.package_saved'));
        } catch {
          showErrorToast(i18n.t('lesson.save_error'), undefined, 5000);
        }
      },
    }),
    updateLessonPackage: build.mutation<
      LessonPackage,
      { id: number; quantity: number; discountPercent: number }
    >({
      query: ({ id, ...body }) => ({
        url: `mentors/me/lesson-packages/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) =>
        parsePackage(unwrapRecord(response)) as LessonPackage,
      invalidatesTags: ['LessonOffering'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('lesson.package_saved'));
        } catch {
          showErrorToast(i18n.t('lesson.save_error'), undefined, 5000);
        }
      },
    }),
  }),
});

export const {
  useGetMyLessonOfferingsQuery,
  useCreateLessonOfferingMutation,
  useUpdateLessonOfferingMutation,
  useCreateLessonPackageMutation,
  useUpdateLessonPackageMutation,
} = lessonOfferingsApi;
